import React from 'react';
import { Linkedin, Youtube, ArrowUpRight, MapPin } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePortfolio } from '../context/PortfolioContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();
  const { contact, loading } = usePortfolio();
  const isContactPage = location.pathname === '/contact';

  const socialIcons = {
    linkedin: Linkedin,
    youtube: Youtube
  };

  const footerLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Insights', path: '/insights' },
    { name: 'Contact', path: '/contact' }
  ];

  const ventures = [
    { name: 'Bondstein Technologies', url: 'https://www.bondstein.com' },
    { name: 'Kyro Solutions', url: 'https://www.kyro.ae' },
    { name: 'Singularity Limited', url: 'https://www.singularitybd.com' }
  ];

  if (loading || !contact) return null;

  return (
    <footer 
      className={`py-16 ${isContactPage ? 'bg-gray-950 border-t border-gray-900' : 'bg-gray-950'}`} 
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <button 
              onClick={() => navigate('/')}
              className="text-2xl font-bold text-white mb-4 block hover:text-gray-300 transition-colors"
            >
              SHAHRUKH
            </button>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Tech Entrepreneur • Innovation Leader • Forbes 30 Under 30 Asia
            </p>
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <MapPin size={14} />
              <span>Dhaka, Bangladesh • Dubai, UAE</span>
            </div>
          </div>

          {/* Navigation Column */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Navigation</h4>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.path}>
                  <button 
                    onClick={() => navigate(link.path)}
                    className="text-gray-400 text-sm hover:text-white transition-colors flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Ventures Column */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Ventures</h4>
            <ul className="space-y-3">
              {ventures.map((venture) => (
                <li key={venture.name}>
                  <a 
                    href={venture.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 text-sm hover:text-white transition-colors flex items-center gap-1 group"
                  >
                    {venture.name}
                    <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect Column */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Connect</h4>
            <div className="flex gap-3 mb-4">
              {contact.social.map((social, index) => {
                const Icon = socialIcons[social.icon];
                return (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-gray-900 rounded-lg hover:bg-gray-800 transition-all duration-300 group hover:scale-105"
                    aria-label={`Follow on ${social.platform}`}
                    data-testid={`footer-social-${social.icon}`}
                  >
                    <Icon size={18} className="text-gray-400 group-hover:text-white transition-colors" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-900 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {currentYear} Mir Shahrukh Islam. All rights reserved.
          </p>
          <p className="text-gray-600 text-xs">
            Building the future of enterprise technology
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
